import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker"
import clsx from "clsx"
import styled from "styled-components"
import "react-datepicker/dist/react-datepicker.css"
import { memo, useMemo, forwardRef } from "react"
import IconCalendarSimple from "../icons/IconCalendarSimple"
import Caption from "components/Typography/Caption"
import { MS_PER_DAY } from "constants/metrics"

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const Icon = styled.div`
  display: flex;
  position: absolute;
  right: 16px;
`

const DateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`

const DateInputWrapper = styled.div`
  & .react-datepicker {
    border: none;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);
  }

  & .react-datepicker__header {
    background: ${(props) => props.theme.palette.white};
    border: none;
  }

  & .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    color: ${(props) => props.theme.palette.primary_dark};
  }

  & .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: ${(props) => props.theme.palette.primary_dark};
    font-size: 11px;
  }

  & .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    border-bottom-color: #6062cb;
  }

  & .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::after {
    border-bottom-color: #fff;
  }

  & .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__month-text--selected,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--in-range,
  .react-datepicker__quarter-text--selected,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__year-text--selected,
  .react-datepicker__year-text--in-selecting-range,
  .react-datepicker__year-text--in-range {
    background-color: #f0f0f0;
  }
`

const DateInput = styled(ReactDatePicker)`
  width: 100%;
  max-width: 240px;
  height: 48px;
  display: block;

  background: transparent;
  border: 1px solid #6062cb;
  border-radius: 8px;
  color: ${(props) => props.theme.palette.primary_dark};
  padding: 16px;
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;

  ::-webkit-input-placeholder {
    color: #002c98;
    font-size: 14px;
    line-height: 18px;
  }

  font-size: 14px;
  line-height: 18px;

  :focus,
  &.shrink {
    outline: none;
  }

  ::placeholder {
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #002c98;
  }
`

const LabelForm = styled.label`
  position: absolute;
  pointer-events: none;
  left: 15px;
  top: -8px;
  padding: 0 4px 0 4px;
  transition: 0.2s ease all;
  color: ${(props) => props.theme.palette.primary_dark};
  background-color: ${(props) => props.theme.palette.white};

  &.error {
    color: ${(props) => props.theme.palette.error};
  }
`

interface DatePickerProps {
  dateFormat: string
  startDate?: Date
  endDate?: Date
  placeholderText?: string
  selectsRange?: boolean
  label: string
  id: string
  shrink?: boolean
  className?: string
  onChange: ReactDatePickerProps["onChange"]
  maxDays?: number
  showMonthYearPicker?: boolean
  startOpen?: boolean
  children?: React.ReactNode
}

const DatePicker = forwardRef<ReactDatePicker, DatePickerProps>((props, ref) => {
  const {
    dateFormat,
    startDate,
    onChange,
    endDate,
    placeholderText,
    selectsRange,
    label,
    id,
    shrink = false,
    className,
    maxDays,
    showMonthYearPicker,
    startOpen = false,
    children
  } = props

  const parsedValue = useMemo(() => {
    if (!startDate && !endDate) return placeholderText

    if (showMonthYearPicker && startDate) {
      return startDate.toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric"
      })
    }

    if (!selectsRange) {
      return startDate?.toLocaleDateString()
    }

    const parsedStartDate = startDate?.toLocaleDateString()
    const parsedEndDate = endDate?.toLocaleDateString()

    return `${parsedStartDate}${parsedEndDate ? ` - ${parsedEndDate}` : ""}`
  }, [startDate, endDate, selectsRange, showMonthYearPicker, placeholderText])

  const maxSelectableDate = useMemo(() => {
    if (!startDate || maxDays == null || endDate) return null

    return new Date(startDate.getTime() + maxDays * MS_PER_DAY)
  }, [startDate, endDate, maxDays])

  return (
    <DateSection>
      <InputWrapper>
        <DateInputWrapper>
          <DateInput
            ref={ref}
            dateFormat={dateFormat}
            selected={selectsRange ? undefined : startDate}
            onChange={onChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange={selectsRange}
            id={id}
            className={clsx(className, { shrink })}
            showMonthYearPicker={showMonthYearPicker}
            value={parsedValue}
            maxDate={maxSelectableDate}
            startOpen={startOpen}
          >
            {children as any}
          </DateInput>
          <LabelForm htmlFor={id} className={clsx(className, { shrink })}>
            <Caption color="primary-dark" size="large">
              {label}
            </Caption>
          </LabelForm>
        </DateInputWrapper>
        <Icon>
          <IconCalendarSimple />
        </Icon>
      </InputWrapper>
    </DateSection>
  )
})

export default memo(DatePicker)
